import React from 'react';
import moment from 'moment-timezone';
import { observer } from 'mobx-react';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { clientChangelogStore } from './ClientChangelogStore';
import { clientStore } from '../ClientStore';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';

const TABLE_COLUMNS = {
	TYPE: 'columnName',
	DESCRIPTION: 'description',
	DATE: 'createdAt',
	USER: 'user.name'
};

const getDisplayDescription = row => {
	const { columnName, description, newValue } = row;

	const descriptionMapping = {
		industry: () => {
			const option = clientStore.industryOptions.find(
				item =>
					item.value === newValue ||
					item.value === Number(newValue) ||
					item.slug === newValue ||
					item.label === newValue
			);
			return option?.label || newValue;
		}
	};

	const mappingFunction = descriptionMapping[columnName];
	return mappingFunction ? mappingFunction() : description || '';
};

const getColumnDisplayName = columnName => {
	if (!columnName) return '';

	const option = clientChangelogStore.typeOptions.find(
		option => option.value === columnName
	);

	if (option) {
		return option.text;
	}

	// Format the column name if not found in options
	// Convert camelCase to Title Case
	const formatted = columnName
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, str => str.toUpperCase())
		.trim();

	return formatted;
};

function ClientChangelogTable({
	data,
	sort,
	onSort,
	isEmpty,
	emptyState,
	loading
}) {
	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;

		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th
					style={{ width: '13%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.TYPE)}>
					Type {isColumnSorted(TABLE_COLUMNS.TYPE)}
				</th>
				<th
					style={{ width: '40%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.DESCRIPTION)}>
					Details {isColumnSorted(TABLE_COLUMNS.DESCRIPTION)}
				</th>
				<th
					style={{ width: '15%' }}
					className="is-sortable"
					onClick={() => handleHeaderClick(TABLE_COLUMNS.DATE)}>
					Date {isColumnSorted(TABLE_COLUMNS.DATE)}
				</th>
				<th style={{ width: '5%' }}>User</th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		return (
			<tr key={row.id}>
				<td className="is-capitalized">
					{getColumnDisplayName(row.columnName) || row.columnName || row.action}
				</td>
				<td style={{ overflowWrap: 'anywhere' }}>
					{getDisplayDescription(row) || row.description}
				</td>
				<td>{moment(row.createdAt).format('lll')}</td>
				<td>
					{row.user && (
						<UserProfileAvatar
							size={32}
							imageUrl={row.user?.imageUrl}
							name={row.user?.name}
							tooltipPosition="top"
						/>
					)}
				</td>
			</tr>
		);
	};

	return (
		<>
			<table className="table is-fullwidth">
				{renderTableHeader()}
				{loading && data.length === 0 ? (
					<AtsTableSkeleton columns={4} rows={6} />
				) : (
					!isEmpty && <tbody>{data.map(renderTableRow)}</tbody>
				)}
			</table>
			{isEmpty ? emptyState : null}
		</>
	);
}

export default observer(ClientChangelogTable);
